import { ColumnDef } from '@tanstack/react-table'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { ProductsHeaderMenu } from 'components/Table'
import { TooltipProps } from 'components/Tooltip'
import { DomainsForTable } from 'features/inventory/domain/components/DomainsTable/types/model'
import {
  DomainProductsAttributes,
  DomainTableSchema,
} from 'features/inventory/domain/forms/DomainTableForm/schema'
import { useAbility } from 'providers/casl'
import { ProductSwitchCell } from '../../components/ProductSwitchCell'
import { useActivateAllDomainsProduct } from '../useActivateAllDomainsProduct/useActivateAllDomainsProduct'
import { useDeactivateAllDomainsProduct } from '../useDeactivateAllDomainsProduct/useDeactivateAllDomainsProduct'
import { UseProductSwitchColumns } from './types'

import type { Product } from 'features/product'

export const useProductSwitchColumns = ({
  products,
  tableItemsCount,
}: UseProductSwitchColumns): ColumnDef<DomainsForTable>[] => {
  const ability = useAbility()
  const { t: tDomain } = useTranslation('features/domain')
  const { t: tProduct } = useTranslation('features/product')
  const { getValues, setValue } = useFormContext<DomainTableSchema>()
  const { activateAllDomainsProduct } = useActivateAllDomainsProduct()
  const { deactivateAllDomainsProduct } = useDeactivateAllDomainsProduct()

  const column = (product: Product): ColumnDef<DomainsForTable> => {
    const noProductAccess: boolean = ability.cannot('access', `Product-${product}`)
    const field = `${product}Enabled` as keyof DomainProductsAttributes

    const cannotAccessOrUpdate: boolean =
      noProductAccess || ability.cannot('update', `Product-${product}`)

    const handleToggleProductState = (enabled: boolean): void => {
      const domainsAttributes = getValues('domainsAttributes')
      if (enabled) {
        activateAllDomainsProduct(product)
      } else {
        deactivateAllDomainsProduct(product)
      }
      Object.keys(domainsAttributes).forEach(domainId => {
        setValue(`domainsAttributes.${domainId}.${field}`, enabled)
      })
    }

    const composeTooltip = (): TooltipProps | undefined => {
      if (noProductAccess) {
        return {
          content: tProduct('featureUnavailable'),
        }
      } else return undefined
    }

    return {
      accessorKey: field,
      cell: props => (
        <ProductSwitchCell
          {...props}
          disabled={cannotAccessOrUpdate}
          product={product}
        />
      ),
      header: tProduct(`product.${product}`),
      meta: {
        disabledHeader: cannotAccessOrUpdate,
        headerMenu: (
          <ProductsHeaderMenu
            i18nResource={tDomain}
            productState={{
              disabledToggle: cannotAccessOrUpdate || tableItemsCount === 0,
              onToggle: handleToggleProductState,
            }}
          />
        ),
        nonClickable: true,
        tooltip: composeTooltip(),
      },
    }
  }

  return products.map((product: Product): ColumnDef<DomainsForTable> => column(product))
}
